import React from 'react';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

import locales from '../../../locales';
import { TReserve } from '../../../types/IBookingStore';
import { TOrder } from '../../../types/IStatisticStore';
import { parsingMergedTables } from '../../../helpers/bookingHelpers';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  infoLabel: {
    fontSize: 12,
    color: '#AAAAAA',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px solid #E5E5E5',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  orderItemName: {
    flex: 1,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  orderItemDetails: {
    display: 'flex',
    gap: theme.spacing(1),
    fontSize: 14,
    color: theme.palette.text.primary,
  },
}));

interface Props {
  info: TReserve;
  order: TOrder;
  moneySymbol: string;
}

const ReserveMobileDetails: React.FC<Props> = ({
  info,
  order,
  moneySymbol,
}) => {
  const classes = useStyles();

  const reserveData = [
    {
      title: locales.t('statistics.reserves.details.reserveData.id'),
      value: info.id || '—',
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.dateOfCreation'),
      value: info.createdDate ? moment(info.createdDate).format('DD MMMM YYYYг. HH:mm') : '—',
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.guests'),
      value: info.persons || '—',
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.table'),
      value: parsingMergedTables(info.tables),
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.date'),
      value: info.date ? moment(info.date).format('DD MMMM YYYYг. HH:mm') : '—',
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.source'),
      value: info.source ? info.source.name : '—',
    },
    {
      title: locales.t('statistics.reserves.details.reserveData.openingDate'),
      value: info.openedTableDate
        ? moment(info.openedTableDate).format('DD MMMM YYYYг. HH:mm')
        : '—',
    },
  ];

  const checkData = [
    {
      title: locales.t('statistics.reserves.details.checkData.tableClosed'),
      value: info.completedDate ? moment(info.completedDate).format('DD MMMM YYYYг. HH:mm') : '—',
    },
    {
      title: locales.t('statistics.reserves.details.checkData.paidType'),
      value: '—',
    },
    {
      title: locales.t('statistics.reserves.details.checkData.paidDate'),
      value: info.paidDate ? moment(info.paidDate).format('DD MMMM YYYYг. HH:mm') : '—',
    },
    {
      title: locales.t('statistics.reserves.details.checkData.paidSum'),
      value: info.receiptAmount ? `${info.receiptAmount} ${moneySymbol}` : '—',
    },
  ];

  return (
    <Box className={classes.container}>
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {locales.t('statistics.reserves.details.reserveData.title')}
        </Typography>
        {reserveData.map((item) => (
          <Box key={item.title} className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              {item.title}
            </Typography>
            <Typography className={classes.infoValue}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {locales.t('statistics.reserves.details.checkData.title')}
        </Typography>
        {checkData.map((item) => (
          <Box key={item.title} className={classes.infoRow}>
            <Typography className={classes.infoLabel}>
              {item.title}
            </Typography>
            <Typography className={classes.infoValue}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {order && order.length > 0 && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {locales.t('statistics.reserves.details.order.title')}
          </Typography>
          {order.map((item, index) => (
            <Box key={index} className={classes.orderItem}>
              <Typography className={classes.orderItemName}>
                {item.name}
              </Typography>
              <Box className={classes.orderItemDetails}>
                <Typography>
                  {item.amount} шт. × {item.resultSum} {moneySymbol}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!!info.comment && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {locales.t('statistics.reserves.details.comment.title')}
          </Typography>
          <Typography className={classes.infoValue}>
            {info.comment}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReserveMobileDetails;
